import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { post, put } from "@common/api/apiShared";
import { IRiskConsequenceDialog } from "./RiskConsequenceDialog";
import { isNullOrWhitespace } from "@common/stringOperations";
import { Result } from "@common/api/result";
import { useRiskAreas } from "@common/hooks/useSelectableItemQueries";
import { RiskConsequenceSaveModel } from "./RiskConsequenceDialog.types";
import { Trans } from "react-i18next";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { getRiskConsequenceCategoryOptions } from "../RiskConsequence.utils";
import { allOptionId, resolveSelectionWithAll } from "../../common/RiskSettings.utils";

export function useRiskConsequenceDialogDataMapping(props: IRiskConsequenceDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskConsequenceCategory } = useTranslation();
    const { isLoading: riskAreasLoading, riskAreas } = useRiskAreas();
    const [errors, setErrors] = useState({ nameError: "", categoryError: "" });
    const [inProgress, setInProgress] = useState(false);
    const [viewModel, setViewModel] = useState<RiskConsequenceSaveModel>(props.selectedRiskConsequence ?? createDefaultRiskConsequenceModel());

    const chosenRiskAreas = viewModel.riskAreaIds.length > 0 ? viewModel.riskAreaIds : [allOptionId];

    const riskAreaOptions = [{ id: allOptionId, name: translateString("allRiskAreas") }, ...(riskAreas ?? [])];

    const categoryOptions = getRiskConsequenceCategoryOptions(translateRiskConsequenceCategory);

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onHideDialog();
                props.onSave(response.value());
                setInProgress(false);
                snackbar.show(createElement(Trans, { i18nKey: "dataCreated", values: { name: viewModel.name } }));
            } else {
                setInProgress(false);
                snackbar.show(translateString("riskConsequenceExists"), "warning");
            }
        },
    });

    function save() {
        return post<string>("/risk-consequences/", toRequestBody());
    }

    function toRequestBody() {
        return {
            name: viewModel.name,
            description: viewModel.description,
            category: Number(viewModel.category),
            riskAreaIds: viewModel.riskAreaIds,
        };
    }

    const editMutation = useDotLegalMutation(update);
    const updateRiskConsequence = () => {
        editMutation.mutateAsync(viewModel.id, {
            onSuccess: (response: Result<string>) => {
                if (response.success()) {
                    snackbar.show(translateString("riskConsequenceUpdated", { name: viewModel.name }));
                    props.onHideDialog();
                    props.onSave("");
                } else {
                    setInProgress(false);
                    snackbar.show(translateString("riskConsequenceExists"), "warning");
                }
            },
        });
    };

    function update(id: string) {
        return put<string>(`/risk-consequences/${id}/`, toRequestBody());
    }

    const setName = (name: string) => {
        setErrors({ ...errors, nameError: isNullOrWhitespace(name) ? translateString("fieldRequired") : "" });
        setViewModel({ ...viewModel, name });
    };

    const setDescription = (description: string) => {
        setViewModel({ ...viewModel, description });
    };

    const setCategory = (category: string | null) => {
        setErrors({ ...errors, categoryError: isNullOrWhitespace(category) ? translateString("fieldRequired") : "" });
        setViewModel({ ...viewModel, category: category ?? "" });
    };

    const setRiskAreas = (values: string[]) => {
        const selection = resolveSelectionWithAll(values, chosenRiskAreas);
        setViewModel({ ...viewModel, riskAreaIds: selection.includes(allOptionId) ? [] : selection });
    };

    const validateAndSave = () => {
        const nameError = isNullOrWhitespace(viewModel.name) ? translateString("fieldRequired") : "";
        const categoryError = isNullOrWhitespace(viewModel.category) ? translateString("fieldRequired") : "";
        if (nameError || categoryError) {
            setErrors({ nameError, categoryError });
            return;
        }

        setInProgress(true);
        if (isEdit()) updateRiskConsequence();
        else saveMutation.mutateAsync();
    };

    function isEdit() {
        return props.selectedRiskConsequence !== undefined && props.selectedRiskConsequence.id !== "";
    }

    return {
        inProgress,
        errors,
        viewModel,
        isEdit,
        setName,
        setDescription,
        setCategory,
        setRiskAreas,
        chosenRiskAreas,
        categoryOptions,
        riskAreaOptions,
        riskAreasLoading,
        validateAndSave,
    };
}

export function createDefaultRiskConsequenceModel(): RiskConsequenceSaveModel {
    return {
        id: "",
        name: "",
        description: "",
        category: "",
        riskAreaIds: [],
    };
}
